/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, ofAction, ofActionSuccessful, Select } from '@ngxs/store';
import { ShowError, ShowInfo, ShowSuccess, ShowWarning, ThrowError } from '../state/notification/notification.actions';
import { AlertComponent } from '../ui/alert/alert.component';
import { LoggerService } from '../../modules/shared/services/logger.service';
import { RouterError } from '@ngxs/router-plugin';
import { IconLoaderService } from '../../modules/shared/services/iconLoader.service';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';
import { AppConfigState } from '../state/appConfig/app-config.state';
import { concatMap, distinctUntilChanged, filter, map, switchMapTo, tap } from 'rxjs/operators';
import { AppPageRoute, MenuBarImageConfig } from '../state/appConfig/app-config.model';
import { Title } from '@angular/platform-browser';
import { ReportDisplayService } from '../../modules/reports/services/report-display.service';
import { AuthState } from '../security/state/authState.state';
import { PageState } from '../state/page/page.state';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HtmlSnackbarComponent } from '../ui/html-snackbar/html-snackbar.component';
import { SnackMessage } from '../state/notification/notification.model';

@Component({
    selector: 'dash-gis-root',
    templateUrl: './root.component.html',
    styleUrls: ['./root.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RootComponent implements AfterViewInit, OnInit, OnDestroy {
    shouldShowNav$: Observable<boolean>;
    showOverlay = true;
    dummyData = [{ a: 1 }, { a: 4 }];

    @Select(AuthState.validated) validated$: Observable<boolean>;
    @Select(AuthState.authenticationInProgress) authInProgress$: Observable<boolean>;
    @Select(AppConfigState.loaded) loaded$: Observable<boolean>;
    @Select(AppConfigState.siteName) siteName$: Observable<string>;
    @Select(AppConfigState.menuBarImage) menuBarImage$: Observable<MenuBarImageConfig>;
    @Select(PageState.loaded) pagesLoaded$: Observable<boolean>;
    @Select(PageState.defaultPage) defaultPage$: Observable<string>;
    @Select(PageState.enabledPages) enabledPages$: Observable<AppPageRoute[]>;
    @Select(PageState.pageEnabled) private _pageEnabled$: Observable<(string) => boolean>;

    private dispose$: Observable<void>;
    private disposeSubject = new Subject<void>();

    private snackQueue: SnackMessage[] = [];
    private snackTrigger$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private title: Title,
        private route: ActivatedRoute,
        private router: Router,
        private iconLoader: IconLoaderService,
        private snackbar: MatSnackBar,
        private dialog: MatDialog,
        private rds: ReportDisplayService,
        private actions$: Actions
    ) {
        this.iconLoader.loadIcons();
        this.dispose$ = this.disposeSubject.asObservable();
        this.shouldShowNav$ = combineLatest([this.enabledPages$, this.validated$]).pipe(
            map(([pages, validated]) => validated && pages.length > 1)
        );

        this.snackTrigger$
            .pipe(
                filter(() => this.snackQueue.length > 0),
                map(() => this.snackQueue.shift()),
                concatMap((snackData) => {
                    let config;
                    switch (snackData.type) {
                        case 'error':
                            config = {
                                data: {
                                    message: snackData.message,
                                    action: {
                                        label: 'Error',
                                        color: 'error',
                                        useCloseButton: true,
                                    },
                                },
                            };
                            break;
                        case 'warning':
                            config = {
                                data: {
                                    message: snackData.message,
                                    action: {
                                        label: 'Warning',
                                        color: 'warn',
                                        useCloseButton: true,
                                    },
                                },
                            };
                            break;
                        case 'success':
                            config = {
                                duration: 5000,
                                data: {
                                    message: snackData.message,
                                    action: {
                                        label: 'Success',
                                        color: 'success',
                                        useCloseButton: false,
                                    },
                                },
                            };
                            break;
                        case 'info':
                            config = {
                                duration: 5000,
                                data: {
                                    message: snackData.message,
                                    action: {
                                        label: 'Info',
                                        color: 'default',
                                        useCloseButton: false,
                                    },
                                },
                            };
                    }
                    return this.snackbar.openFromComponent(HtmlSnackbarComponent, config).afterDismissed();
                }),
                tap(() => this.snackTrigger$.next())
            )
            .subscribe();
    }

    ngOnInit() {
        this.loaded$
            .pipe(
                filter((b) => b),
                switchMapTo(this.siteName$)
            )
            .subscribe((siteName) => this.title.setTitle(siteName));

        this.actions$.pipe(ofActionSuccessful(ThrowError)).subscribe((action: ThrowError) => {
            this.logger.error(action.exception);
            if (action.exception?.message) {
                this.dialog.open(AlertComponent, {
                    width: '350px',
                    minHeight: '300px',
                    data: action.exception.message,
                });
            }
        });

        this.actions$.pipe(ofActionSuccessful(ShowError)).subscribe((action: ShowError) => {
            this.addSnack({ message: action.message, type: 'error' });
        });

        this.actions$.pipe(ofActionSuccessful(ShowWarning)).subscribe((action: ShowWarning) => {
            this.addSnack({ message: action.message, type: 'warning' });
        });

        this.actions$.pipe(ofActionSuccessful(ShowSuccess)).subscribe((action: ShowSuccess) => {
            this.addSnack({ message: action.message, type: 'success' });
        });

        this.actions$.pipe(ofActionSuccessful(ShowInfo)).subscribe((action: ShowInfo) => {
            this.addSnack({ message: action.message, type: 'info' });
        });

        this.actions$.pipe(ofAction(RouterError)).subscribe((action: RouterError<any, any>) => {
            this.logger.error('RouterError:', action);
        });

        this.validated$
            .pipe(
                distinctUntilChanged(),
                filter((b) => b)
            )
            .subscribe(() => this.rds.init());
    }

    ngAfterViewInit() {
        // when drag/drop is enable (for file uploads), these prevent
        // dropping a file outside the target area and having it load over the page
        window.addEventListener(
            'dragover',
            (e) => {
                e.preventDefault();
                e.dataTransfer.effectAllowed = 'none';
                e.dataTransfer.dropEffect = 'none';
            },
            false
        );

        window.addEventListener(
            'drop',
            (e) => {
                e.preventDefault();
                e.dataTransfer.effectAllowed = 'none';
                e.dataTransfer.dropEffect = 'none';
            },
            false
        );
    }

    pageEnabled$(page: string) {
        return this._pageEnabled$.pipe(map((fn) => fn(page)));
    }

    ngOnDestroy() {
        this.disposeSubject.next();
    }

    addSnack(message: SnackMessage) {
        const last = this.snackQueue.length;
        this.snackQueue.push(message);
        if (last === 0) {
            this.snackTrigger$.next();
        }
    }
}
